import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Trash2 } from 'lucide-react';
import Layout from '../components/Layout';
import { messagingApi, ChatMessage } from '../api';
import { useAuth } from '../context/AuthContext';

const Chat: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const threadId = useMemo(() => Number(params.threadId), [params.threadId]);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const threadTitle = 'Chat';

  const scrollToBottom = () => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const load = async () => {
      setError('');
      setIsLoading(true);
      try {
        if (!threadId) {
          setError('Invalid thread');
          return;
        }
        const data = await messagingApi.getThread(threadId);
        setMessages(data);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load chat');
      } finally {
        setIsLoading(false);
        setTimeout(scrollToBottom, 50);
      }
    };

    load();
  }, [threadId]);

  const handleSend = async () => {
    setError('');
    const trimmed = body.trim();
    if (!trimmed) return;

    setIsSending(true);
    try {

      const sent = await messagingApi.sendMessage(threadId, trimmed);
      setMessages((prev) => [...prev, sent]);
      setBody('');
      setTimeout(scrollToBottom, 50);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDelete = async (messageId: number) => {
    setDeletingId(messageId);
    setError('');
    try {
      await messagingApi.deleteMessage(threadId, messageId);
      setMessages((current) => current.filter((message) => message.id !== messageId));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete message');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Layout>
      <div className="pb-20 space-y-4">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center space-x-2 text-sm text-green-200 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </button>
          <p className="text-sm font-semibold text-white">{threadTitle}</p>
          <Link to="/messages" className="text-sm text-emerald-600 font-medium hover:underline">
            Threads
          </Link>
        </div>

        {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>}

        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 min-h-[50vh]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : messages.length === 0 ? (
            <p className="text-sm text-gray-500">No messages yet. Say hi.</p>
          ) : (
            <div className="space-y-3">
              {messages.map((m) => {
                const isMine = m.sender_id === (user?.id || 0);
                return (
                  <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                        isMine ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {m.body}
                      {isMine && (
                        <button
                          type="button"
                          onClick={() => handleDelete(m.id)}
                          disabled={deletingId === m.id}
                          aria-label="Delete message"
                          title="Delete message"
                          className="ml-2 inline-flex align-middle text-white/60 hover:text-white disabled:opacity-40"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100 flex items-center space-x-2">
          <input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message"
            className="flex-1 px-3 py-2 bg-gray-50 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={isSending}
            className="inline-flex items-center justify-center w-11 h-11 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition disabled:opacity-50"
            title="Send"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default Chat;
